import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { AlertTriangle, CheckCircle, Clock, Copy01, Wallet02, XClose } from '@untitled-ui/icons-react'

import { BillingService } from '../billing.service'
import { formatIDR } from '../billing.format'
import PaymentMethodPicker from '../components/PaymentMethodPicker'
import PaymentQr from '@src/components/PaymentQr'
import { IS_PROD } from '@src/runtimeConfig'

/**
 * "Confirm plan changes" — the dialog for moving between packages on a company
 * that is already subscribed (Figma 15195:147290 for the scheduled case,
 * 15195:146799 for the immediate one).
 *
 * EVERY NUMBER COMES FROM THE SERVER, via `/billing/subscription/change-preview`,
 * which runs the same `quoteChange()` the switch itself will run. Nothing is
 * recomputed in the browser, so the figure approved here and the ledger entry
 * that follows cannot disagree.
 *
 * TWO SHAPES, because there are two behaviours:
 *
 *   UPGRADE   — happens now. Pro-rata credit for the unused remainder of the
 *               current period comes off the new package's price; the rest is
 *               paid from SALDO (atomic) or, when saldo is short, straight
 *               through a payment GATEWAY (VA/QRIS rendered in-portal). The
 *               gateway leg is only offered when the subscription's expiry is
 *               far enough away that its short-lived invoice cannot outlive the
 *               renewal boundary (the server enforces this and answers 422 when
 *               it is too close — the modal then falls back to saldo).
 *   DOWNGRADE — happens at the next boundary and costs nothing today.
 */
function Row({ label, sub, value, tone }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div>
        <p className={`text-sm ${tone === 'muted' ? 'text-gray-light/600' : 'text-gray-light/900'}`}>
          {label}
        </p>
        {sub ? <p className="mt-0.5 text-xs text-gray-light/500">{sub}</p> : null}
      </div>
      <p
        className={`shrink-0 whitespace-nowrap text-sm font-semibold ${
          tone === 'credit' ? 'text-success/700' : 'text-gray-light/900'
        }`}
      >
        {value}
      </p>
    </div>
  )
}

Row.propTypes = {
  label: PropTypes.node,
  sub: PropTypes.node,
  value: PropTypes.node,
  tone: PropTypes.string,
}

Row.defaultProps = { label: null, sub: null, value: null, tone: null }

function formatDate(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

const STEP = { CONFIRM: 'confirm', PAY: 'pay' }

function ConfirmChangeModal({ open, companyId, pkg, currentPackage, onClose, onDone }) {
  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Payment selection (upgrade only). payMode 'saldo' = inline atomic debit;
  // 'gateway' = a `method` wire code + the in-portal VA/QR step below.
  const [payMode, setPayMode] = useState('saldo')
  const [method, setMethod] = useState(null)

  // Gateway payment step.
  const [step, setStep] = useState(STEP.CONFIRM)
  const [gatewayPayment, setGatewayPayment] = useState(null)
  const [changeResult, setChangeResult] = useState(null)
  const [checking, setChecking] = useState(false)
  const [paid, setPaid] = useState(false)

  useEffect(() => {
    if (!open || !companyId || !pkg) return undefined
    let alive = true
    setLoading(true)
    setError(null)
    setQuote(null)
    setStep(STEP.CONFIRM)
    setGatewayPayment(null)
    setChangeResult(null)
    setPaid(false)
    setMethod(null)
    BillingService.previewChange(companyId, { pricing_package_id: pkg.id })
      .then((res) => {
        if (!alive) return
        const q = res?.data ?? res
        setQuote(q)
        // Default to saldo when it covers the charge; when it is short, start on
        // the gateway so the user is not staring at a disabled button with no
        // hint that another way exists.
        setPayMode(q?.direction === 'upgrade' && Number(q?.saldo_shortfall_idr || 0) > 0 ? 'gateway' : 'saldo')
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.message || 'Failed to calculate the plan change.')
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [open, companyId, pkg])

  const upgrade = quote?.direction === 'upgrade'
  const boundary = Number(quote?.due_at_boundary_idr || 0)
  const dueToday = Number(quote?.due_today_idr || 0)
  const shortfall = Number(quote?.saldo_shortfall_idr || 0)
  const effective = formatDate(quote?.effective_at)
  const usingGateway = upgrade && dueToday > 0 && payMode === 'gateway'

  const submit = useCallback(async () => {
    if (!companyId || !pkg) return
    setSubmitting(true)
    setError(null)
    try {
      const payload = { pricing_package_id: pkg.id }
      if (usingGateway) {
        payload.payment_method = 'gateway'
        payload.payment_methods = method ? [method] : undefined
      }
      const res = await BillingService.changePlan(companyId, payload)
      const body = res?.data ?? res
      if (usingGateway && body?.gateway_payment) {
        setChangeResult(body)
        setGatewayPayment(body.gateway_payment)
        setStep(STEP.PAY)
        return
      }
      // Saldo (or a fully credit-covered upgrade / downgrade): done immediately.
      onDone(body)
    } catch (err) {
      const status = err?.response?.status
      const msg = err?.response?.data?.message
      // 422 = too close to the renewal boundary for a gateway invoice. Fall the
      // UI back to saldo so the customer has a way through.
      if (status === 422 && usingGateway) {
        setPayMode('saldo')
        setError(msg || 'Too close to renewal — the upgrade must be paid from saldo.')
      } else {
        setError(msg || 'Failed to change the plan.')
      }
    } finally {
      setSubmitting(false)
    }
  }, [companyId, pkg, usingGateway, method, onDone])

  // Poll the upgrade invoice while the gateway step is open, so a webhook-
  // confirmed payment flips the modal to "paid" (the swap is applied server-side
  // on settlement) without the customer refreshing.
  const invoiceId = gatewayPayment?.invoice_id || null
  const refreshStatus = useCallback(async () => {
    if (!companyId || !invoiceId) return
    setChecking(true)
    try {
      const res = await BillingService.getInvoiceDetail(companyId, invoiceId)
      const inv = res?.data ?? res
      if (inv?.status === 'paid') setPaid(true)
    } catch {
      /* transient — the next tick retries */
    } finally {
      setChecking(false)
    }
  }, [companyId, invoiceId])

  useEffect(() => {
    if (step !== STEP.PAY || paid || !invoiceId) return undefined
    const t = setInterval(refreshStatus, 5000)
    return () => clearInterval(t)
  }, [step, paid, invoiceId, refreshStatus])

  if (!open || !pkg) return null

  const boundaryCostsMore =
    !upgrade && boundary > 0 && currentPackage && boundary > Number(currentPackage.harga_periode_idr || 0)

  const copy = (text) => {
    if (!text) return
    try {
      navigator.clipboard?.writeText(String(text))
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-light/900/40 p-4">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-light/200 p-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-light/900">
              {step === STEP.PAY
                ? 'Complete payment'
                : upgrade
                  ? 'Confirm plan upgrade'
                  : 'Confirm plan change'}
            </h2>
            <p className="mt-1 text-sm text-gray-light/600">
              {currentPackage ? `${currentPackage.name} → ${pkg.name}` : pkg.name}
              {pkg.period_label ? ` (${pkg.period_label})` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-light/400">
            <XClose className="size-5" />
          </button>
        </div>

        {/* ─── Gateway payment step ─────────────────────────────────────────── */}
        {step === STEP.PAY ? (
          <div className="space-y-4 p-5">
            <div className="flex items-center gap-3 rounded-lg border border-gray-light/200 p-4">
              <span
                className={`flex size-10 items-center justify-center rounded-full ${
                  paid ? 'bg-success/100 text-success/600' : 'bg-warning/100 text-warning/600'
                }`}
              >
                {paid ? <CheckCircle className="size-5" /> : <Clock className="size-5" />}
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-light/900">
                  {paid ? 'Payment received — your plan has been upgraded' : 'Awaiting payment'}
                </p>
                <p className="text-sm text-gray-light/600">
                  {paid
                    ? 'Your plan is active now.'
                    : 'Your plan is upgraded automatically once the payment is verified.'}
                </p>
              </div>
            </div>

            {!paid && (gatewayPayment?.qr_string || gatewayPayment?.qr_url) ? (
              <PaymentQr qrString={gatewayPayment?.qr_string} qrUrl={gatewayPayment?.qr_url} />
            ) : null}

            {!paid && gatewayPayment?.va_number ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-light/200 px-4 py-3">
                <span className="text-sm text-gray-light/700">
                  {gatewayPayment.va_bank ? `${gatewayPayment.va_bank} VA number` : 'VA number'}
                </span>
                <button
                  type="button"
                  onClick={() => copy(gatewayPayment.va_number)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-light/900 hover:text-brand/700"
                >
                  {gatewayPayment.va_number}
                  <Copy01 className="size-4 text-brand/600" />
                </button>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              {!paid && gatewayPayment?.payment_url && (!(gatewayPayment?.va_number || gatewayPayment?.qr_string || gatewayPayment?.qr_url) || !IS_PROD) ? (
                <button
                  type="button"
                  onClick={() => window.open(gatewayPayment.payment_url, '_blank', 'noopener')}
                  className="rounded-lg border border-gray-light/300 px-4 py-2.5 text-sm font-semibold text-gray-light/700"
                >
                  {gatewayPayment?.va_number || gatewayPayment?.qr_string || gatewayPayment?.qr_url
                    ? 'Simulate payment (dev)'
                    : 'Open payment page'}
                </button>
              ) : null}
              {!paid ? (
                <button
                  type="button"
                  onClick={refreshStatus}
                  disabled={checking}
                  className="rounded-lg border border-brand/300 bg-white px-4 py-2.5 text-sm font-semibold text-brand/700 disabled:opacity-50"
                >
                  {checking ? 'Checking…' : 'Check status'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onDone(changeResult)}
                className="rounded-lg bg-brand/600 px-4 py-2.5 text-sm font-semibold text-white"
              >
                {paid ? 'Done' : 'Close'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5">
              {loading ? (
                <div className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-9 animate-pulse rounded bg-gray-light/100" />
                  ))}
                </div>
              ) : null}

              {!loading && quote ? (
                <>
                  {upgrade ? (
                    <div className="divide-y divide-gray-light/200">
                      <Row label={`${pkg.name} plan`} value={formatIDR(quote.new_price_idr)} />
                      {quote.credit_idr > 0 ? (
                        <Row
                          label="Prorated credit"
                          sub={`${quote.remaining_days} of ${quote.window_days} days of ${currentPackage?.name || 'the current plan'} unused`}
                          value={`− ${formatIDR(quote.applied_idr)}`}
                          tone="credit"
                        />
                      ) : null}
                      <Row label="Amount due" value={formatIDR(dueToday)} />
                      {quote.excess_idr > 0 ? (
                        <Row
                          label="Remaining credit returned to saldo"
                          sub="Usable for enquiries — no further PPN is charged"
                          value={`+ ${formatIDR(quote.excess_idr)}`}
                          tone="credit"
                        />
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-gray-light/200 bg-gray-light/50 p-3 text-sm text-gray-light/700">
                        Your <span className="font-semibold">{currentPackage?.name}</span> plan stays
                        active until <span className="font-semibold">{effective}</span>, then
                        switches automatically to <span className="font-semibold">{pkg.name}</span>.
                      </div>
                      <div className="divide-y divide-gray-light/200">
                        <Row label="Paid now" value={formatIDR(0)} />
                        {boundary > 0 ? (
                          <Row
                            label={`Invoice on ${effective}`}
                            sub="Deducted automatically from saldo"
                            value={formatIDR(boundary)}
                          />
                        ) : null}
                      </div>
                    </div>
                  )}

                  {/* ─── Payment method (upgrade with something to pay) ─────── */}
                  {upgrade && dueToday > 0 ? (
                    <div className="mt-4 space-y-3 border-t border-gray-light/200 pt-4">
                      <p className="text-sm font-semibold text-gray-light/900">Payment method</p>
                      <button
                        type="button"
                        onClick={() => shortfall === 0 && setPayMode('saldo')}
                        disabled={shortfall > 0}
                        className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left ${
                          payMode === 'saldo'
                            ? 'border-brand/300 bg-brand/25 ring-1 ring-brand/300'
                            : 'border-gray-light/200'
                        } disabled:cursor-not-allowed disabled:opacity-60`}
                      >
                        <span className="flex items-center gap-2">
                          <Wallet02 className="size-4 text-gray-light/600" />
                          <span className="text-sm font-medium text-gray-light/900">Saldo</span>
                        </span>
                        <span className="text-xs text-gray-light/600">
                          {shortfall > 0 ? `short by ${formatIDR(shortfall)}` : 'active immediately'}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPayMode('gateway')}
                        className={`w-full rounded-lg border px-4 py-3 text-left text-sm font-medium ${
                          payMode === 'gateway'
                            ? 'border-brand/300 bg-brand/25 ring-1 ring-brand/300 text-gray-light/900'
                            : 'border-gray-light/200 text-gray-light/900'
                        }`}
                      >
                        Pay via transfer / QRIS
                      </button>

                      {payMode === 'gateway' ? (
                        <PaymentMethodPicker
                          companyId={companyId}
                          amountIdr={dueToday}
                          value={method}
                          autoSelect={false}
                          onChange={(wire) => setMethod(wire)}
                        />
                      ) : null}
                    </div>
                  ) : null}

                  {boundaryCostsMore ? (
                    <div className="mt-4 flex gap-2 rounded-lg border border-warning/300 bg-warning/50 p-3">
                      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning/700" />
                      <p className="text-xs text-warning/700">
                        This plan is a lower tier, but its invoice is{' '}
                        <span className="font-semibold">larger</span> than your current plan (
                        {formatIDR(currentPackage.harga_periode_idr)}) because its period is longer.
                        Make sure your saldo is sufficient on {effective} — if it falls short, the
                        switch is canceled and your current plan renews as usual.
                      </p>
                    </div>
                  ) : null}
                </>
              ) : null}

              {quote?.golongan_locked ? (
                <div className="mt-4 rounded-md border border-warning/300 bg-warning/50 p-3 text-sm text-warning/700">
                  {quote.golongan_lock_reason ||
                    'Your business segment changed — plan changes are locked until renewal or until the subscription is canceled.'}
                </div>
              ) : null}

              {error ? (
                <div className="mt-4 rounded-md border border-error/200 bg-error/50 p-3 text-sm text-error/700">
                  {error}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-light/200 p-5">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-light/300 px-4 py-2.5 text-sm font-semibold text-gray-light/700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={
                  submitting ||
                  loading ||
                  !quote ||
                  quote.golongan_locked ||
                  (upgrade && dueToday > 0 && payMode === 'saldo' && shortfall > 0) ||
                  (usingGateway && !method)
                }
                className="rounded-lg bg-brand/600 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-light/300"
              >
                {submitting
                  ? 'Processing…'
                  : !upgrade
                    ? 'Schedule'
                    : usingGateway
                      ? 'Continue to payment'
                      : `Pay ${formatIDR(dueToday)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

ConfirmChangeModal.propTypes = {
  open: PropTypes.bool,
  companyId: PropTypes.string,
  pkg: PropTypes.object,
  currentPackage: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
}

ConfirmChangeModal.defaultProps = {
  open: false,
  companyId: null,
  pkg: null,
  currentPackage: null,
  onClose: () => {},
  onDone: () => {},
}

export default ConfirmChangeModal
